import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  LayoutDashboard,
  Map,
  LayoutGrid,
  ListChecks,
  MountainSnow,
  Trophy,
  Store,
  BarChart3,
  Plug,
  CreditCard,
  ShieldAlert,
  Triangle,
  Users,
  Flag,
  MessageSquarePlus,
  Menu,
  X,
  Footprints,
  Route,
  Compass,
  Link2,
  type LucideIcon,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useIsPro } from "../hooks/useIsPro";
import { ImportProgressBanner } from "../components/ImportProgressBanner";
import { StravaReconnectBanner } from "../components/StravaReconnectBanner";
import { FeedbackDialog } from "../components/FeedbackDialog";
import { getWallet } from "../api/store";
import { fetchProfileStats } from "../api/profile";
import { NotificationsBell } from "../components/NotificationsBell";
import { fetchNotifications } from "../api/social";
import { fetchPartnerDashboard } from "../api/partners";
import { ProfileIdentity } from "../components/ProfileIdentity";
import { LevelUpOverlay } from "../components/LevelUpOverlay";
import { AnnouncementOverlay } from "../components/AnnouncementOverlay";
import { getBannerAsset } from "../lib/cosmetics";
import { STORE_THEME_SKINS, applySiteTheme, setBaseTheme, storedBaseTheme } from "../lib/theme";
import { readPendingGroupJoin } from "../lib/pending-group-join";
import "./appshell.css";

type NavEntry = { id: string; label: string; icon: LucideIcon; to: string };

const NAV_PRIMARY: NavEntry[] = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, to: "/dashboard" },
  { id: "map", label: "Map", icon: Map, to: "/map" },
];

const NAV_COLLECT: NavEntry[] = [
  { id: "collections", label: "Collections", icon: LayoutGrid, to: "/collections" },
  { id: "regions", label: "Regions", icon: MountainSnow, to: "/regions" },
  { id: "achievements", label: "Achievements", icon: Trophy, to: "/achievements" },
  { id: "lists", label: "My lists", icon: ListChecks, to: "/lists" },
];

const NAV_COMMUNITY: NavEntry[] = [
  { id: "feed", label: "Feed", icon: Users, to: "/feed" },
  { id: "groups", label: "Groups", icon: Flag, to: "/groups" },
];

const NAV_TOOLKIT: Array<{ id: string; label: string; icon: LucideIcon; to: string; proTag?: boolean }> = [
  { id: "where-to-start", label: "Where to start", icon: Compass, to: "/where-to-start", proTag: true },
  { id: "route-analyser", label: "Check a route", icon: Route, to: "/route-analyser" },
];

const NAV_SECONDARY: Array<{
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  // Plain page navigation instead of a router Link — see NavItem below.
  external?: boolean;
}> = [
  { id: "stats", label: "Statistics", icon: BarChart3, to: "/statistics" },
  { id: "sources", label: "Connected sources", icon: Plug, to: "/connected-sources" },
  { id: "activities", label: "Activities", icon: Footprints, to: "/activities" },
  { id: "store", label: "Store", icon: Store, to: "/store" },
  { id: "plan", label: "Plan & Billing", icon: CreditCard, to: "/plan" },
  // Guides (/guides, statically prerendered outside the SPA) deliberately has
  // no sidebar entry; it is reached from the public site. If it returns, it
  // must be `external: true` (plain <a>, never a TanStack Link).
];

// Wallet balance in the sidebar footer. Renders a stable, fixed-height row so
// loading never shifts the layout, and fails silently if the endpoint errors.
function WalletBalance() {
  const { data, isError } = useQuery({
    queryKey: ["economy/wallet"],
    queryFn: getWallet,
    staleTime: 60_000,
    retry: false,
  });

  return (
    <div
      style={{
        height: 26,
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "0 10px",
        marginTop: 6,
      }}
    >
      {data != null && !isError && (
        <>
          <Triangle
            size={10}
            fill="currentColor"
            aria-hidden="true"
            style={{ color: "var(--gold-400)", flexShrink: 0 }}
          />
          <span
            className="eyebrow tnum"
            style={{ fontSize: 10, color: "var(--gold-400)", letterSpacing: "0.1em" }}
          >
            {data.waypoints.toLocaleString("en-GB")} waypoints
          </span>
        </>
      )}
    </div>
  );
}

function NavItem({
  label,
  icon: Icon,
  to,
  active,
  external,
  tag,
}: {
  label: string;
  icon: LucideIcon;
  to: string;
  active: boolean;
  // Renders a plain <a> instead of a router Link — for destinations outside
  // the SPA route tree (e.g. the statically prerendered /guides section).
  external?: boolean;
  // Small outline tag at the row end (e.g. "Pro" for free accounts).
  tag?: string;
}) {
  const style: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 11,
    width: "100%",
    padding: "9px 12px",
    borderRadius: "var(--radius-md)",
    border: "1px solid " + (active ? "var(--border-gold)" : "transparent"),
    background: active ? "var(--accent-soft)" : "transparent",
    color: active ? "var(--text-accent)" : "var(--text-secondary)",
    font: "var(--type-body-sm)",
    fontWeight: active ? 600 : 500,
    fontSize: 14,
    textDecoration: "none",
    transition: "background 0.15s ease, color 0.15s ease",
  };

  if (external) {
    return (
      <a href={to} style={style}>
        <Icon size={18} strokeWidth={active ? 2.3 : 2} />
        {label}
      </a>
    );
  }

  return (
    <Link to={to} style={style}>
      <Icon size={18} strokeWidth={active ? 2.3 : 2} />
      <span style={{ flex: 1 }}>{label}</span>
      {tag && (
        <span
          style={{
            font: "700 9px var(--font-mono)",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "var(--text-secondary)",
            border: "1px solid var(--border-strong)",
            borderRadius: 4,
            padding: "1px 5px",
          }}
        >
          {tag}
        </span>
      )}
    </Link>
  );
}

function NavList({ items, pathname, label }: { items: NavEntry[]; pathname: string; label?: string }) {
  return (
    <nav aria-label={label} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {items.map((n) => (
        <NavItem
          key={n.id}
          label={n.label}
          icon={n.icon}
          to={n.to}
          active={pathname === n.to || (n.to !== "/map" && pathname.startsWith(`${n.to}/`))}
        />
      ))}
    </nav>
  );
}

function NavHeading({ children }: { children: ReactNode }) {
  return (
    <div
      className="eyebrow"
      style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--text-faint)", padding: "14px 12px 6px" }}
    >
      {children}
    </div>
  );
}

export default function AppShell({ children }: { children?: ReactNode } = {}) {
  const { user } = useAuth();
  const { location } = useRouterState();
  const pathname = location.pathname;
  const [showFeedback, setShowFeedback] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  // Group invite resume (spec 32): a visitor who opened /groups/join/{code}
  // signed out and then signed up (Strava-first lands on /dashboard) is sent
  // back to the join page. The join page clears the pending code. /plan is
  // skipped so a new sign-up's Pro checkout return isn't interrupted.
  const navigate = useNavigate();
  useEffect(() => {
    if (!user || pathname.startsWith("/groups/join/") || pathname.startsWith("/plan")) return;
    const pending = readPendingGroupJoin();
    if (!pending) return;
    void navigate({
      to: "/groups/join/$code",
      params: { code: pending.code },
      search: pending.ref ? { ref: pending.ref } : {},
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pathname]);

  // Mobile drawer: close on navigation and on Escape.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navOpen]);

  // Same query key the profile page uses, so equipping cosmetics updates the
  // sidebar card immediately (the equip mutation invalidates it).
  const profileStatsQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
    staleTime: 60_000,
    retry: false,
  });
  const equipped = profileStatsQuery.data?.equipped;

  // Creators with a partner link get a "Partner" entry; everyone else gets an
  // empty list back and sees nothing. Same key as PartnerPage.
  const partnerQuery = useQuery({
    queryKey: ["partner"],
    queryFn: fetchPartnerDashboard,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const isPartner = (partnerQuery.data?.codes.length ?? 0) > 0;

  // Same key as NotificationsBell, so this shares its cache and polling. On
  // narrow screens the bell sits inside the closed drawer; the menu button
  // carries the unread dot instead.
  const notificationsQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: () => fetchNotifications(),
    refetchInterval: 60_000,
    retry: false,
  });
  const hasUnread = (notificationsQuery.data?.unreadCount ?? 0) > 0;
  const bannerSrc = getBannerAsset(equipped?.banner?.metadata.assetKey);

  const isPro = user?.plan === "pro";
  const { isPro: hasPro } = useIsPro();

  // Site-wide theme (WS-3 phase 2): an equipped store theme re-skins the
  // whole app; otherwise the server-side per-profile base choice (ATL-28),
  // else the local choice (shared with the landing-page toggle), defaulting
  // to dark. The server preference is mirrored into localStorage so the
  // pre-fetch first paint matches on this device. Store skins deliberately
  // never touch localStorage so unequipping falls straight back to the base.
  const equippedSkin = equipped?.mapTheme
    ? STORE_THEME_SKINS[equipped.mapTheme.slug]
    : undefined;
  const serverBaseTheme = profileStatsQuery.data?.themePreference ?? undefined;
  useEffect(() => {
    if (serverBaseTheme) setBaseTheme(serverBaseTheme);
    applySiteTheme(equippedSkin ?? serverBaseTheme ?? storedBaseTheme());
  }, [equippedSkin, serverBaseTheme]);

  return (
    <div className="appshell">
      {/* Mobile top bar (hidden on desktop via appshell.css) */}
      <header className="appshell-topbar">
        <button
          type="button"
          className="appshell-menu-btn"
          onClick={() => setNavOpen(true)}
          aria-label={hasUnread ? "Open navigation, unread notifications" : "Open navigation"}
        >
          <Menu size={22} />
          {hasUnread && <span className="appshell-menu-dot" aria-hidden="true" />}
        </button>
        <Link to="/dashboard" style={{ display: "flex", alignItems: "center", userSelect: "none" }}>
          <img
            className="atlas-logo"
            src="/atlas-logo.svg"
            alt="Atlas"
            draggable={false}
            style={{ height: 22 }}
          />
        </Link>
      </header>

      {/* Mobile drawer backdrop */}
      <div
        className={`appshell-backdrop${navOpen ? " open" : ""}`}
        onClick={() => setNavOpen(false)}
        aria-hidden="true"
      />

      {/* Sidebar */}
      <aside className={`appshell-sidebar${navOpen ? " open" : ""}`}>
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", padding: "0 8px 22px" }}>
          <Link
            to="/dashboard"
            style={{ display: "flex", alignItems: "center", userSelect: "none" }}
            aria-label="Atlas dashboard"
          >
            <img
              className="atlas-logo"
              src="/atlas-logo.svg"
              alt="Atlas"
              draggable={false}
              style={{ height: 48 }}
            />
          </Link>
          <button
            type="button"
            className="appshell-close"
            onClick={() => setNavOpen(false)}
            aria-label="Close navigation"
          >
            <X size={20} />
          </button>
        </div>

        {/* Primary nav, then grouped sections */}
        <NavList items={NAV_PRIMARY} pathname={pathname} />
        <NavHeading>Collect</NavHeading>
        <NavList items={NAV_COLLECT} pathname={pathname} label="Collect" />
        <NavHeading>Community</NavHeading>
        <NavList items={NAV_COMMUNITY} pathname={pathname} label="Community" />

        {/* Toolkit */}
        <NavHeading>Toolkit</NavHeading>
        <nav aria-label="Toolkit" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {NAV_TOOLKIT.map((n) => (
            <NavItem
              key={n.id}
              label={n.label}
              icon={n.icon}
              to={n.to}
              active={pathname === n.to}
              tag={n.proTag && !hasPro && !user?.isAdmin ? "Pro" : undefined}
            />
          ))}
        </nav>

        <NavHeading>You</NavHeading>
        <nav aria-label="You" style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {NAV_SECONDARY.map((n) => (
            <NavItem
              key={n.id}
              label={n.label}
              icon={n.icon}
              to={n.to}
              active={
                !n.external &&
                (pathname === n.to ||
                  ((n.id === "activities" || n.id === "store") && pathname.startsWith(`${n.to}/`)))
              }
              external={n.external}
            />
          ))}
          {isPartner && (
            <NavItem label="Partner" icon={Link2} to="/partner" active={pathname === "/partner"} />
          )}
          {user?.isAdmin && (
            <NavItem
              label="Admin"
              icon={ShieldAlert}
              to="/admin"
              active={pathname.startsWith("/admin")}
            />
          )}
          <button
            type="button"
            onClick={() => setShowFeedback(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 11,
              width: "100%",
              padding: "9px 12px",
              borderRadius: "var(--radius-md)",
              border: "1px solid transparent",
              background: "transparent",
              color: "var(--text-secondary)",
              font: "var(--type-body-sm)",
              fontWeight: 500,
              fontSize: 14,
              fontFamily: "var(--font-sans)",
              cursor: "pointer",
              textAlign: "left",
              transition: "background 0.15s ease, color 0.15s ease",
            }}
          >
            <MessageSquarePlus size={18} strokeWidth={2} />
            Send feedback
          </button>
        </nav>

        {/* User card */}
        <div style={{ marginTop: "auto" }}>
          <NotificationsBell />
          <Link
            to="/profile"
            style={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: 10,
              borderRadius: "var(--radius-md)",
              background: pathname === "/profile" ? "var(--accent-soft)" : "var(--surface-card)",
              border: `1px solid ${pathname === "/profile" ? "var(--border-gold)" : "var(--border-subtle)"}`,
              textDecoration: "none",
              transition: "background 0.15s ease, border-color 0.15s ease",
            }}
          >
            {/* Equipped banner as the card background, dimmed for contrast */}
            {bannerSrc && (
              <>
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url("${bannerSrc}")`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    inset: 0,
                    background: "color-mix(in srgb, var(--bg-app) 55%, transparent)",
                  }}
                />
              </>
            )}
            <div style={{ position: "relative", minWidth: 0, flex: 1 }}>
              <ProfileIdentity
                name={user?.name ?? "Explorer"}
                avatarUrl={user?.avatarUrl}
                title={equipped?.title}
                badges={equipped?.badges}
                size="sm"
                subtitle={
                  <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-400)" }}>
                    {isPro ? "Pro" : "Free"}
                  </div>
                }
              />
            </div>
          </Link>
          <WalletBalance />
        </div>
      </aside>

      {/* Main content */}
      <div className="appshell-main">
        {/* Big imports take an hour to match; say so everywhere until done. */}
        <ImportProgressBanner />
        {/* Strava refused the connection: ask for a reconnect everywhere. */}
        <StravaReconnectBanner />
        {children ?? <Outlet />}
      </div>

      {/* Level-up fanfare — profile/stats reports pendingLevelUp until acked,
          so this fires once per level no matter which page the user lands on. */}
      <LevelUpOverlay pending={profileStatsQuery.data?.pendingLevelUp ?? null} />

      {/* Session-start announcements — server tracks dismissals, so each one
          shows once per user no matter which page they land on. */}
      <AnnouncementOverlay />

      {showFeedback && <FeedbackDialog onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
